import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Text,
  AccordionIcon,
  useBreakpointValue,
  Skeleton,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'

import RenderPdf from '~/react/components/RenderPdf'
import {ReceiptLongIcon} from '~/react/icons'
import {useRails} from '~/react/contexts/rails'

import {SMALL_SCREEN_BREAKPOINT} from '../../constants'
import {PayablePropType} from '../../../../propTypes'
import PdfFallback from '../PdfFallback'

export const PdfContent = ({payable, isLoading}) => {
  const {t} = useTranslation('pointsOnPayments')

  const {
    currentSupplier: {id: supplierId},
  } = useRails()

  const [pdfUrl, setPdfUrl] = useState(null)

  const [refreshKey, setRefreshKey] = useState(0)

  const smallScreen = useBreakpointValue({
    base: true,
    [SMALL_SCREEN_BREAKPOINT]: false,
  })

  const [hasPdf, setHasPdf] = useState(null)

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!pdfUrl) {
      return
    }

    fetch(pdfUrl, {method: 'HEAD'})
      .then((response) => setHasPdf(response.ok))
      .catch(() => setHasPdf(false))
  }, [pdfUrl, isLoading])

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1)
  }

  useEffect(() => {
    if (!payable) {
      return
    }
    const url = `/points_on_payments/api/suppliers/${supplierId}/bills/${payable.id}/pdf?refresh=${refreshKey}`
    setPdfUrl(url)
  }, [payable, supplierId, refreshKey])

  const isCheckingPdf = hasPdf === null

  if (smallScreen) {
    if (!hasPdf) {
      return null
    }

    return (
      <Accordion allowToggle>
        <AccordionItem mx="0" border="none">
          <AccordionButton px="0">
            <ReceiptLongIcon mr="xxs" />
            <Text textStyle="body-copy">
              {t(
                'pointsOnPayments:paymentWizard.payableContentPanel.pdfContent.label',
              )}
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px="0">
            <Skeleton isLoaded={!isLoading} height="100%">
              <Box background="mld.neutral.50" p="md" height="100%">
                <RenderPdf url={pdfUrl} toolbar={0} view="FitH" />
              </Box>
            </Skeleton>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    )
  }

  return (
    <Skeleton isLoaded={!isLoading && !isCheckingPdf} height="100%">
      <Box background="mld.neutral.50" p="md" height="100%">
        {hasPdf ? (
          <RenderPdf
            url={pdfUrl}
            toolbar={0}
            view="FitH"
            breakpoint={SMALL_SCREEN_BREAKPOINT}
          />
        ) : (
          <PdfFallback onRefresh={handleRefresh} />
        )}
      </Box>
    </Skeleton>
  )
}

PdfContent.defaultProps = {
  payable: null,
}

PdfContent.propTypes = {
  payable: PayablePropType,
  isLoading: PropTypes.bool.isRequired,
}
